/**
 * useMultiLocaleUpload - Hook for multi-locale upload with localStorage preference
 *
 * Manages user preference for applying uploaded media to multiple locales.
 * Stores preference in localStorage to remember user's choice.
 *
 * Features:
 * - Check if dialog should be shown (based on locale count and saved preference)
 * - Save user preference ('all' or 'current')
 * - Clear preference (reset to always ask)
 *
 * Sprint: multi-locale-upload
 * Phase: 2 - Hook Implementation (TDD GREEN)
 */

import { useState, useCallback } from 'react';

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * localStorage key for storing multi-locale upload preference
 */
export const MULTI_LOCALE_STORAGE_KEY = 'onboarding-multi-locale-upload-preference';

/**
 * Valid preference values
 */
const VALID_PREFERENCES = ['all', 'current'] as const;

// ============================================================================
// TYPES
// ============================================================================

/**
 * User preference for multi-locale upload
 * - 'all': Always apply to all locales
 * - 'current': Always apply to current locale only
 * - null: No preference (always show dialog)
 */
export type MultiLocalePreference = 'all' | 'current' | null;

/**
 * Return type for useMultiLocaleUpload hook
 */
export interface UseMultiLocaleUploadReturn {
  /** Saved preference from localStorage */
  savedPreference: MultiLocalePreference;

  /** Check if dialog should be shown for given locales */
  shouldShowDialog: (availableLocales: string[]) => boolean;

  /** Save preference to localStorage */
  savePreference: (preference: 'all' | 'current') => void;

  /** Clear preference from localStorage */
  clearPreference: () => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Safely read preference from localStorage
 * Returns null for invalid or missing values
 */
function readPreferenceFromStorage(): MultiLocalePreference {
  try {
    const stored = localStorage.getItem(MULTI_LOCALE_STORAGE_KEY);
    if (stored && VALID_PREFERENCES.includes(stored as 'all' | 'current')) {
      return stored as MultiLocalePreference;
    }
    return null;
  } catch {
    // localStorage not available (SSR, private browsing, etc.)
    return null;
  }
}

/**
 * Safely write preference to localStorage
 */
function writePreferenceToStorage(preference: 'all' | 'current'): void {
  try {
    localStorage.setItem(MULTI_LOCALE_STORAGE_KEY, preference);
  } catch {
    // localStorage not available - silently fail
  }
}

/**
 * Safely remove preference from localStorage
 */
function removePreferenceFromStorage(): void {
  try {
    localStorage.removeItem(MULTI_LOCALE_STORAGE_KEY);
  } catch {
    // localStorage not available - silently fail
  }
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * useMultiLocaleUpload - Hook for managing multi-locale upload preference
 *
 * @returns Object with preference state and control functions
 *
 * @example
 * ```tsx
 * const { savedPreference, shouldShowDialog, savePreference, clearPreference } = useMultiLocaleUpload();
 *
 * // Check if dialog should be shown
 * if (shouldShowDialog(availableLocales)) {
 *   setShowDialog(true);
 * } else if (savedPreference === 'all') {
 *   applyToAllLocales();
 * } else {
 *   applyToCurrentLocale();
 * }
 *
 * // In dialog handlers
 * const handleApplyAll = (rememberChoice: boolean) => {
 *   if (rememberChoice) savePreference('all');
 *   applyToAllLocales();
 * };
 * ```
 */
export function useMultiLocaleUpload(): UseMultiLocaleUploadReturn {
  // Initialize state from localStorage
  const [savedPreference, setSavedPreference] = useState<MultiLocalePreference>(
    () => readPreferenceFromStorage()
  );

  /**
   * Check if dialog should be shown for given locales
   *
   * Returns false if:
   * - Single locale or no locales (no need for dialog)
   * - User has saved preference (auto-apply based on preference)
   *
   * Returns true if:
   * - Multiple locales AND no saved preference
   */
  const shouldShowDialog = useCallback(
    (availableLocales: string[]): boolean => {
      // Single locale or empty - never show dialog
      if (availableLocales.length <= 1) {
        return false;
      }

      // Multiple locales but preference saved - don't show dialog
      if (savedPreference !== null) {
        return false;
      }

      // Multiple locales, no preference - show dialog
      return true;
    },
    [savedPreference]
  );

  /**
   * Save preference to localStorage and update state
   */
  const savePreference = useCallback((preference: 'all' | 'current') => {
    writePreferenceToStorage(preference);
    setSavedPreference(preference);
  }, []);

  /**
   * Clear preference from localStorage and reset state
   */
  const clearPreference = useCallback(() => {
    removePreferenceFromStorage();
    setSavedPreference(null);
  }, []);

  return {
    savedPreference,
    shouldShowDialog,
    savePreference,
    clearPreference,
  };
}

export default useMultiLocaleUpload;
